var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useCallback, useMemo, useState } from 'react';
var toComparableString = function (value) {
    var _a;
    if (value == null)
        return '';
    if (typeof value === 'string')
        return value;
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    return (_a = JSON.stringify(value)) !== null && _a !== void 0 ? _a : '';
};
/** Normalize to YYYY-MM-DD for inclusive range compares. */
var toDateKey = function (value) {
    if (value == null || value === '')
        return null;
    var raw = JSON.stringify(value);
    if (/^\d{4}-\d{2}-\d{2}/.test(raw))
        return raw.slice(0, 10);
    var d = new Date(raw);
    if (Number.isNaN(d.getTime()))
        return null;
    return d.toISOString().slice(0, 10);
};
var compareValues = function (a, b) {
    if (a == null && b == null)
        return 0;
    if (a == null)
        return 1;
    if (b == null)
        return -1;
    if (typeof a === 'number' && typeof b === 'number')
        return a - b;
    var as = toComparableString(a);
    var bs = toComparableString(b);
    if (as < bs)
        return -1;
    if (as > bs)
        return 1;
    return 0;
};
/**
 * Owns client-side table state (sort + per-column filters) for in-memory row sets.
 * Derives enum facets from the unfiltered rows. Pagination stays with the DataGrid.
 */
export function useClientTableFilter(_a) {
    var rows = _a.rows, columns = _a.columns, defaultSort = _a.defaultSort, getValue = _a.getValue;
    var _b = useState(defaultSort !== null && defaultSort !== void 0 ? defaultSort : null), sort = _b[0], setSort = _b[1];
    var _c = useState({}), filters = _c[0], setFilters = _c[1];
    var configByField = useMemo(function () {
        var map = {};
        columns.forEach(function (col) { return (map[col.field] = col); });
        return map;
    }, [columns]);
    var readValue = useCallback(function (row, field) {
        var custom = getValue === null || getValue === void 0 ? void 0 : getValue(row, field);
        if (custom !== undefined)
            return custom;
        return row[field];
    }, [getValue]);
    var applyFilter = useCallback(function (field, update) {
        if ('sort' in update) {
            setSort(update.sort ? { field: field, order: update.sort } : function (prev) { return ((prev === null || prev === void 0 ? void 0 : prev.field) === field ? null : prev); });
        }
        if ('selectedValues' in update || 'contains' in update || 'dateRange' in update) {
            setFilters(function (prev) {
                var _a;
                var next = __assign(__assign({}, prev), (_a = {}, _a[field] = __assign({}, prev[field]), _a));
                if ('selectedValues' in update)
                    next[field].selectedValues = update.selectedValues;
                if ('contains' in update)
                    next[field].contains = update.contains;
                if ('dateRange' in update)
                    next[field].dateRange = update.dateRange;
                return next;
            });
        }
    }, []);
    var resetFilters = useCallback(function () {
        setFilters({});
        setSort(defaultSort !== null && defaultSort !== void 0 ? defaultSort : null);
    }, [defaultSort]);
    var facets = useMemo(function () {
        var _a;
        var result = {};
        for (var _i = 0, columns_1 = columns; _i < columns_1.length; _i++) {
            var col = columns_1[_i];
            if (col.filter !== 'enum')
                continue;
            var counts = new Map();
            for (var _b = 0, rows_1 = rows; _b < rows_1.length; _b++) {
                var row = rows_1[_b];
                var raw = toComparableString(readValue(row, col.field));
                if (!raw)
                    continue;
                counts.set(raw, ((_a = counts.get(raw)) !== null && _a !== void 0 ? _a : 0) + 1);
            }
            result[col.field] = Array.from(counts.entries()).map(function (_a) {
                var value = _a[0], count = _a[1];
                return ({ value: value, count: count });
            });
        }
        return result;
    }, [columns, rows, readValue]);
    var processedRows = useMemo(function () {
        var next = rows.filter(function (row) {
            var _a;
            for (var _i = 0, _b = Object.entries(filters); _i < _b.length; _i++) {
                var _c = _b[_i], field = _c[0], state = _c[1];
                var config = configByField[field];
                if (!(config === null || config === void 0 ? void 0 : config.filter))
                    continue;
                var value = readValue(row, field);
                if (config.filter === 'text' && state.contains) {
                    if (!toComparableString(value).toLowerCase().includes(state.contains.toLowerCase()))
                        return false;
                }
                if (config.filter === 'enum' && ((_a = state.selectedValues) === null || _a === void 0 ? void 0 : _a.length)) {
                    if (!state.selectedValues.includes(toComparableString(value)))
                        return false;
                }
                if (config.filter === 'date' && state.dateRange && (state.dateRange.from || state.dateRange.to)) {
                    var key = toDateKey(value);
                    if (!key)
                        return false;
                    if (state.dateRange.from && key < state.dateRange.from)
                        return false;
                    if (state.dateRange.to && key > state.dateRange.to)
                        return false;
                }
            }
            return true;
        });
        if (sort) {
            var field_1 = sort.field, order_1 = sort.order;
            next = __spreadArray([], next, true).sort(function (a, b) {
                var cmp = compareValues(readValue(a, field_1), readValue(b, field_1));
                return order_1 === 'desc' ? -cmp : cmp;
            });
        }
        return next;
    }, [rows, filters, sort, configByField, readValue]);
    var sortModel = useMemo(function () { return (sort ? [{ field: sort.field, sort: sort.order }] : []); }, [sort]);
    var getFilterState = useCallback(function (field) {
        var _a, _b, _c;
        var config = configByField[field];
        var state = filters[field];
        return {
            sort: (sort === null || sort === void 0 ? void 0 : sort.field) === field ? sort.order : null,
            selectedValues: (_a = state === null || state === void 0 ? void 0 : state.selectedValues) !== null && _a !== void 0 ? _a : null,
            contains: (_b = state === null || state === void 0 ? void 0 : state.contains) !== null && _b !== void 0 ? _b : null,
            dateRange: (_c = state === null || state === void 0 ? void 0 : state.dateRange) !== null && _c !== void 0 ? _c : null,
            kind: config === null || config === void 0 ? void 0 : config.filter,
            sortable: config === null || config === void 0 ? void 0 : config.sortable,
        };
    }, [configByField, filters, sort]);
    var getAllValues = useCallback(function (field) {
        var _a;
        var config = configByField[field];
        return ((_a = facets[field]) !== null && _a !== void 0 ? _a : []).map(function (_a) {
            var _b, _c;
            var value = _a.value, count = _a.count;
            return ({
                value: value,
                label: (_c = (_b = config === null || config === void 0 ? void 0 : config.getLabel) === null || _b === void 0 ? void 0 : _b.call(config, value)) !== null && _c !== void 0 ? _c : value,
                count: count,
            });
        });
    }, [configByField, facets]);
    return {
        processedRows: processedRows,
        sortModel: sortModel,
        getFilterState: getFilterState,
        getAllValues: getAllValues,
        applyFilter: applyFilter,
        resetFilters: resetFilters,
    };
}
